import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import type { BlastRadiusScoreDocument } from '../api-utils/blast';
import EbrDetailPage from '../pages/EbrDetailPage';
import RouteMessage from './RouteMessage';
import { blastScoreRouteId, loadBlastScore } from './resolvers';
import { useGoBack } from './useGoBack';

interface EntityRouteProps {
  /** Show the entity in the Investigation Workspace graph. */
  onOpenInGraph: (id: string, name: string) => void;
}

type Remote = { id: string; score: BlastRadiusScoreDocument | null; failed: boolean };

/**
 * `/entities/:id`, the Effective Blast Radius detail of one element. The score comes from the
 * link that opened it (kept in the history entry) or, for a shared link, from the API.
 */
const EntityRoute: React.FC<EntityRouteProps> = ({ onOpenInGraph }) => {
  const { id = '' } = useParams();
  const location = useLocation();
  const goBack = useGoBack();
  const opened = (location.state as { score?: BlastRadiusScoreDocument } | null)?.score;
  const local = opened && blastScoreRouteId(opened) === id ? opened : null;
  const [remote, setRemote] = useState<Remote | null>(null);
  const [latest, setLatest] = useState<BlastRadiusScoreDocument | null>(null);
  // Try again reads once more: the count runs the effect again, and clearing `remote` shows the loading message meanwhile.
  const [attempt, setAttempt] = useState(0);
  const retry = () => {
    setRemote(null);
    setAttempt((count) => count + 1);
  };

  useEffect(() => {
    if (local) return;
    let alive = true;
    loadBlastScore(id).then(
      (score) => alive && setRemote({ id, score, failed: false }),
      () => alive && setRemote({ id, score: null, failed: true })
    );
    return () => {
      alive = false;
    };
  }, [id, local, attempt]);

  const score = latest && blastScoreRouteId(latest) === id ? latest : local ?? (remote?.id === id ? remote.score : null);
  if (score) {
    return (
      <EbrDetailPage
        score={score}
        onBack={goBack}
        onOpenInGraph={onOpenInGraph}
        onScoreUpdated={setLatest}
      />
    );
  }
  if (remote?.id !== id) {
    return <RouteMessage key="busy" busy title="Loading blast radius…" onBack={attempt > 0 ? goBack : undefined} />;
  }
  return (
    <RouteMessage
      key="failed"
      title={remote.failed ? 'Could not load the blast radius' : 'Blast radius not found'}
      detail={
        remote.failed
          ? 'Nothing was changed. Try again in a moment.'
          : 'This entity has no blast radius score in this organization yet.'
      }
      onBack={goBack}
      onRetry={remote.failed ? retry : undefined}
    />
  );
};

export default EntityRoute;
